import { Database, Trash2, MailCheck, Clock3 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { GlassCard } from "@/components/GlassCard";
import { Reveal } from "@/components/Reveal";
import { SUPPORT_SERVER_URL } from "@/config";

const CARDS = [
  {
    icon: Database,
    title: "What may be stored",
    text: "Server and user IDs, your module configuration (log channels, security settings, ticket panels, level roles), moderation and security logs you enabled, and ticket transcripts.",
  },
  {
    icon: Trash2,
    title: "What deletion removes",
    text: "A deletion request removes your server's stored configuration, logs, transcripts, and associated feature data from Xevon's systems.",
  },
  {
    icon: MailCheck,
    title: "How to request",
    text: "Open a ticket in the official support server from the account that owns the server. State the server name and ID. The team verifies ownership before processing.",
  },
  {
    icon: Clock3,
    title: "Processing expectations",
    text: "Requests are processed manually after ownership verification. You'll receive confirmation in the ticket once deletion is complete. Removing the bot stops further data collection immediately.",
  },
];

export default function ServerDeletion() {
  useSEO({
    title: "Server Data Deletion",
    description: "How to request deletion of your server's stored Xevon data — what is stored, what is removed, and the process.",
    path: "/server-deletion",
  });

  return (
    <>
      <PageHeader
        kicker="Your Data"
        title="Server data deletion."
        description="You stay in control of your server's data. Here's exactly what's stored, what deletion removes, and how to request it."
      />
      <section className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="server-deletion-page">
        <div className="grid gap-4 md:grid-cols-2">
          {CARDS.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.06}>
              <GlassCard className="h-full p-8" data-testid={`deletion-card-${i}`}>
                <card.icon className="h-6 w-6 text-paper-100" aria-hidden="true" />
                <h2 className="mt-5 font-display text-lg font-semibold text-paper-100">{card.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-paper-400 md:text-base">{card.text}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <GlassCard className="mt-12 p-10 text-center" hover={false}>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-paper-100">
              Request deletion
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-paper-400">
              Deletion requests are handled through the support server so ownership can be verified
              securely. Open a ticket and the team will take it from there.
            </p>
            <a
              href={SUPPORT_SERVER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="deletion-request-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
            >
              Open the Support Server
            </a>
          </GlassCard>
        </Reveal>
      </section>
    </>
  );
}
